import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import { classifyAuthError, EXPIRED_LINK_MESSAGE } from "@/lib/authMessages"

const RESET_REDIRECT_URL = "https://cat.translyx.app/reset-password"

type LinkStatus = "checking" | "ready" | "expired"

// Supabase reports a bad or used link as error / error_code in the URL hash
// (or in the query string, depending on the flow).
function urlCarriesAuthError(): boolean {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""))
  const query = new URLSearchParams(window.location.search)
  return [hash, query].some(
    (p) => p.has("error") || p.has("error_code") || p.has("error_description"),
  )
}

export default function ResetPasswordPage() {
  const [status, setStatus] = useState<LinkStatus>("checking")
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState("")
  const [resendError, setResendError] = useState("")
  const [resent, setResent] = useState(false)
  const [resending, setResending] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (urlCarriesAuthError()) {
      setStatus("expired")
      return
    }

    let settled = false
    const settle = (next: LinkStatus) => {
      if (settled) return
      settled = true
      setStatus(next)
    }

    // Supabase puts the token in the URL and signs the person in with it. If no
    // session appears shortly, the link cannot be used.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN" || event === "INITIAL_SESSION")) {
        settle("ready")
      }
    })

    let timer: ReturnType<typeof setTimeout> | undefined
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) settle("ready")
      else timer = setTimeout(() => settle("expired"), 1500)
    }).catch(() => settle("expired"))

    return () => {
      subscription.unsubscribe()
      if (timer) clearTimeout(timer)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    if (password !== confirm) return setError("Passwords do not match")
    if (password.length < 6) return setError("Password must be at least 6 characters")
    setLoading(true)
    const { error } = await supabase.auth.updateUser({ password })
    setLoading(false)
    if (error) {
      const issue = classifyAuthError(error)
      if (issue.kind === "expired_link") return setStatus("expired")
      return setError(issue.text)
    }
    setSuccess(true)
    setTimeout(() => navigate("/"), 2000)
  }

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault()
    setResendError("")
    if (!email.trim()) return setResendError("Please enter your email address.")
    setResending(true)
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: RESET_REDIRECT_URL,
    })
    setResending(false)
    if (error) return setResendError(classifyAuthError(error).text)
    setResent(true)
  }

  const inputClass =
    "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
  const buttonClass =
    "w-full bg-gold-500 hover:bg-gold-400 text-slate-900 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50"

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded-xl shadow-md w-full max-w-md">
        {status === "checking" && (
          <p className="text-gray-500 text-sm text-center">Checking your reset link…</p>
        )}

        {status === "expired" && (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Link no longer valid</h1>
            <p className="text-gray-700 text-sm mb-6">{EXPIRED_LINK_MESSAGE}</p>
            {resent ? (
              <p className="text-green-700 text-sm">
                If an account exists for that email address, a new link is on its way. Check your inbox.
              </p>
            ) : (
              <form onSubmit={handleResend} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder="you@example.com"
                    required
                  />
                </div>
                {resendError && <p className="text-red-600 text-sm">{resendError}</p>}
                <button type="submit" disabled={resending} className={buttonClass}>
                  {resending ? "Sending…" : "Send a new link"}
                </button>
              </form>
            )}
            <p className="text-center text-sm text-gray-500 mt-6">
              <a href="/login" className="text-gold-700 hover:text-gold-600 font-medium">Back to sign in</a>
            </p>
          </>
        )}

        {status === "ready" && (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Set new password</h1>
            <p className="text-gray-500 text-sm mb-6">Enter your new password below.</p>
            {success ? (
              <p className="text-green-600 text-center">Password updated! Redirecting...</p>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className={inputClass}
                    placeholder="At least 6 characters"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className={inputClass}
                    placeholder="Repeat your password"
                    required
                  />
                </div>
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <button type="submit" disabled={loading} className={buttonClass}>
                  {loading ? "Updating..." : "Update password"}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  )
}
